import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconDownload, IconEdit, IconFinance, IconTrash } from '../components/icons';
import { useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';
import { formatMoney } from '../utils/format';

// TS port of the source (Falah) app's pages/CustomerInvoiceDetail.jsx.
//
// Renamed vs. source: `invoice.donor` -> `invoice.member` (CustomerInvoice
// belongs to Member, not a Donor model — see api/app/Models/CustomerInvoice.php
// and CustomerInvoiceController::show()'s `member.user:...` eager load).
// IconPayment doesn't exist in this app's icon set (components/icons.tsx) —
// IconFinance (a currency/money glyph) substitutes for the "Record a
// payment" action button.
//
// `status` has no editable UI here (matching source): CustomerInvoiceController
// ::validated() doesn't accept a `status` field at all — it's set internally
// (draft on create, partially_paid/paid via storeReceipt) — so unlike
// CustomerQuotationDetail, the inline edit form never offers a status select.
// Dropped vs. source: 'donation'/'zakat' type labels and their conditional
// category/Member detail rows — mosque-specific concepts with no VNPF
// equivalent. Category and Member are shown unconditionally below instead.
const TYPE_LABELS: Record<string, string> = {
  general: 'General',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  issued: 'Issued',
  sent: 'Sent',
  partially_paid: 'Partially Paid',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
};

function statusBadgeClass(status: string): string {
  if (status === 'paid') return 'badge-normal';
  if (['overdue', 'cancelled'].includes(status)) return 'badge-important';
  return 'badge-neutral';
}

interface InvoiceFormState {
  customer_name: string;
  amount: string | number;
  invoice_date: string;
  due_date: string;
  notes: string;
}

const emptyForm: InvoiceFormState = { customer_name: '', amount: '', invoice_date: '', due_date: '', notes: '' };

interface Receipt {
  id: number;
  paid_amount: number;
}

interface InvoiceRecord {
  id: number;
  invoice_number: string;
  type: string;
  category: string | null;
  customer_name: string;
  amount: number;
  invoice_date: string;
  due_date: string | null;
  notes: string | null;
  status: string;
  customer_quotation: { id: number; quotation_number: string } | null;
  member: { id: number; user: { first_name: string; last_name: string } | null } | null;
  receipts?: Receipt[];
}

type FormErrors = Record<string, string[]>;

export function CustomerInvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { settings } = useConfig();
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  const [invoice, setInvoice] = useState<InvoiceRecord | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<InvoiceFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});

  const refresh = async () => {
    const res = await api.get<InvoiceRecord>(`/customer-invoices/${id}`);
    setInvoice(res.data);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const startEdit = (current: InvoiceRecord) => {
    setForm({
      customer_name: current.customer_name,
      amount: current.amount,
      invoice_date: current.invoice_date,
      due_date: current.due_date ?? '',
      notes: current.notes ?? '',
    });
    setErrors({});
    setEditing(true);
  };

  useEffect(() => {
    if (invoice && searchParams.get('edit') === '1') {
      startEdit(invoice);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice]);

  const totalPaid = (invoice?.receipts ?? []).reduce((sum, r) => sum + Number(r.paid_amount), 0);
  const balance = invoice ? Number(invoice.amount) - totalPaid : 0;

  const set =
    (key: keyof InvoiceFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.put(`/customer-invoices/${id}`, form);
      setEditing(false);
      await refresh();
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    }
  };

  const downloadInvoice = () => invoice && downloadFile(api, `/customer-invoices/${id}/pdf`, `${invoice.invoice_number}.pdf`);

  const remove = async () => {
    if (!invoice) return;
    if (!(await confirmDelete(`Delete invoice ${invoice.invoice_number}?`))) return;
    try {
      await api.delete(`/customer-invoices/${id}`);
      navigate('/revenue/invoices');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  if (!invoice) return null;

  return (
    <>
      <header className="page-header">
        {editing && <h1>Invoice: {invoice.invoice_number}</h1>}
        {!editing && (
          <div className="page-header-actions">
            <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/revenue/invoices')} />
            <IconButton
              icon={IconFinance}
              label="Record a payment"
              to={`/revenue/receipts/new?invoiceId=${invoice.id}`}
              hidden={balance <= 0}
            />
            <IconButton icon={IconDownload} label="Download PDF" onClick={downloadInvoice} />
            <IconButton icon={IconEdit} label="Edit details" onClick={() => startEdit(invoice)} />
            <IconButton
              icon={IconTrash}
              label="Delete"
              variant="danger-ghost"
              onClick={remove}
              hidden={invoice.status !== 'draft'}
            />
          </div>
        )}
      </header>

      <div className="card">
        {!editing ? (
          <div>
            <div className="detail-summary">
              <div>
                <div className="detail-summary-title">{invoice.invoice_number}</div>
                <span className={`badge ${statusBadgeClass(invoice.status)}`}>
                  {STATUS_LABELS[invoice.status] ?? invoice.status}
                </span>
                <div className="detail-summary-amount">{fmtMoney(invoice.amount)}</div>
                <div className="detail-summary-service">{invoice.customer_name}</div>
              </div>
            </div>

            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-item-label">Type</span>
                <span className="detail-item-value">{TYPE_LABELS[invoice.type] ?? invoice.type}</span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Quotation</span>
                {invoice.customer_quotation ? (
                  <Link className="detail-item-value" to={`/revenue/quotations/${invoice.customer_quotation.id}`}>
                    {invoice.customer_quotation.quotation_number}
                  </Link>
                ) : (
                  <span className="detail-item-value empty">No quotation</span>
                )}
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Customer name</span>
                <span className="detail-item-value">{invoice.customer_name}</span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Category</span>
                <span className={`detail-item-value${invoice.category ? '' : ' empty'}`}>
                  {invoice.category || 'Not set'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Member</span>
                <span className={`detail-item-value${invoice.member?.user ? '' : ' empty'}`}>
                  {invoice.member?.user
                    ? `${invoice.member.user.first_name} ${invoice.member.user.last_name}`
                    : 'Not a member'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Amount ({settings.currency})</span>
                <span className="detail-item-value">{fmtMoney(invoice.amount)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Invoice date</span>
                <span className="detail-item-value">{invoice.invoice_date}</span>
              </div>
              <div className="detail-item">
                <span className="detail-item-label">Due date</span>
                <span className={`detail-item-value${invoice.due_date ? '' : ' empty'}`}>
                  {invoice.due_date || 'Not set'}
                </span>
              </div>
            </div>

            <div className="detail-item detail-item-full">
              <span className="detail-item-label">Notes</span>
              {invoice.notes ? (
                <p className="detail-notes">{invoice.notes}</p>
              ) : (
                <span className="detail-item-value empty">Not provided</span>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Customer name *
              <input value={form.customer_name} onChange={set('customer_name')} required />
              {errors.customer_name && <small className="error">{errors.customer_name[0]}</small>}
            </label>
            <label>
              Amount ({settings.currency}) *
              <input type="number" step="0.01" min="0.01" value={form.amount} onChange={set('amount')} required />
              {errors.amount && <small className="error">{errors.amount[0]}</small>}
            </label>
            <label>
              Invoice date *
              <input type="date" value={form.invoice_date} onChange={set('invoice_date')} required />
            </label>
            <label>
              Due date
              <input type="date" value={form.due_date} onChange={set('due_date')} />
            </label>
            <label className="span-2">
              Notes
              <textarea rows={2} value={form.notes} onChange={set('notes')} />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
